import { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { isNil } from 'lodash';
import { add, isBefore, startOfToday, sub } from 'date-fns';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useBulkQuery } from 'redux/api_slice/availability';
import { setBookingFormInitialValues } from 'redux/slice/experience';
import {
  get7DaysStraight,
  isSeasonalityAvailable,
  transferToPriceMap,
  transferToSeatMap,
} from './helpers';

const CONSTANT_UNIT = 1;

function AvailabilityQuickView({
  resourceId,
  cardUrl,
  offCanvasMode,
  pricingMode,
  productPrice,
  productSalePrice,
}) {
  const defaultPrice = productSalePrice || productPrice || null;
  const dispatch = useDispatch();
  const [firstDay, setFirstDay] = useState(startOfToday());
  const [dateObj, setDateObj] = useState(get7DaysStraight(firstDay));
  const inputEl = useRef(null);
  const seasonalityReady = useRef(false);

  const onChangeFirstDay = (date) => {
    setFirstDay(date);
    setDateObj(get7DaysStraight(date));
  };

  useEffect(() => {
    window.ecom.need_wait_react_mounted = false;
    inputEl.current?.addEventListener('change', (e) => {
      const t = e.target.value;
      onChangeFirstDay(new Date(t));
    });
  }, []);

  const dateArray = Object.keys(dateObj);
  const fromDate = dateArray[0];
  const toDate = dateArray[dateArray.length - 1];
  const { data: bookability, isFetching: loading } = useBulkQuery(
    {
      resourceIds: [resourceId],
      units: CONSTANT_UNIT,
      fromDate,
      toDate,
      includeSeasonalities: pricingMode ? 1 : 0,
    },
    {
      skip: isNil(resourceId),
    }
  );
  const seatMap = transferToSeatMap(bookability?.[resourceId] || {});

  const onClickNextWeek = () => {
    const nextFirstDay = add(firstDay, { days: 7 });
    onChangeFirstDay(nextFirstDay);
  };

  const onClickPreviousWeek = () => {
    const nextFirstDay = sub(firstDay, { days: 7 });
    const today = startOfToday();
    if (isBefore(nextFirstDay, today)) {
      onChangeFirstDay(today);
    } else {
      onChangeFirstDay(nextFirstDay);
    }
  };

  const setDateTimeInReduxCb = (date) => () => {
    dispatch(setBookingFormInitialValues({ date }));
  };
  if (
    pricingMode &&
    defaultPrice === null &&
    seasonalityReady.current === false
  ) {
    seasonalityReady.current = isSeasonalityAvailable(
      bookability?.[resourceId] || {}
    );
    if (seasonalityReady.current === false) {
      return null;
    }
  }

  const priceMap = transferToPriceMap(
    bookability?.[resourceId] || {},
    defaultPrice
  );
  return (
    <>
      <input
        className="d-none availability-quick-view__hidden-input"
        ref={inputEl}
        aria-label="hidden-input"
      />
      <div
        className={classnames(
          'availability-quick-view mt-3 mb-5 d-flex justify-content-between align-items-center text-center',
          { 'availability-quick-view__pricing-mode': pricingMode }
        )}
      >
        <i
          role="button"
          aria-label="previous-week"
          className="p-1 fas fa-chevron-left"
          onClick={onClickPreviousWeek}
        />
        {Object.keys(dateObj).map((key) => {
          const date = dateObj[key];
          const seatNumber = seatMap[key];
          const { price, available } = priceMap[key] || {};
          return (
            <a
              className={classnames('d-block text-body text-decoration-none', {
                'pe-none opacity-50': available === false,
              })}
              key={key}
              data-bs-toggle={offCanvasMode ? 'offcanvas' : undefined}
              href={
                offCanvasMode
                  ? '#data-booking-drawer'
                  : `${cardUrl}?date=${key}`
              }
              onClick={offCanvasMode ? setDateTimeInReduxCb(key) : undefined}
            >
              <span className="d-block availability-quick-view__date">
                {date.dayOfMonth}
              </span>
              {loading || undefined === seatNumber ? (
                <span className="d-block availability-quick-view__skeleton-frame mx-auto">
                  <Skeleton />
                </span>
              ) : (
                <>
                  {pricingMode && (
                    <span className="d-block fw-bold text-xxx-small">
                      {price}
                    </span>
                  )}
                  {!pricingMode && (
                    <span
                      className={classnames(
                        'd-block availability-quick-view__seat-box mx-auto text-white text-decoration-none',
                        {
                          'bg-success': seatNumber >= 5,
                          'bg-warning': seatNumber < 5 && seatNumber > 0,
                          'bg-danger opacity-75': seatNumber === 0,
                        }
                      )}
                    >
                      {seatNumber}
                    </span>
                  )}
                </>
              )}

              {!pricingMode && (
                <span className="d-block text-uppercase">{date.dayOfWeek}</span>
              )}
            </a>
          );
        })}
        <i
          role="button"
          aria-label="next-week"
          className="p-1 fas fa-chevron-right"
          onClick={onClickNextWeek}
        />
      </div>
    </>
  );
}

export default AvailabilityQuickView;

AvailabilityQuickView.propTypes = {
  resourceId: PropTypes.number,
  cardUrl: PropTypes.string,
  offCanvasMode: PropTypes.bool,
  pricingMode: PropTypes.bool,
  productPrice: PropTypes.number,
  productSalePrice: PropTypes.number,
};
